"use client";

import Image from "next/image";
import Link from "next/link";
import { useDeferredValue, useEffect, useMemo, useState } from "react";
import { DOMAIN_COLORS } from "@/lib/domains";
import { cardMatchesQuery } from "@/lib/card-search";
import { merchantLinksForCard } from "@/lib/merchant-links";
import type { CardRecord } from "@/lib/types";

function compareBySetThenNumber(a: CardRecord, b: CardRecord): number {
  const setA = (a.setId ?? "").toUpperCase();
  const setB = (b.setId ?? "").toUpperCase();
  if (setA !== setB) {
    if (!setA) return 1;
    if (!setB) return -1;
    return setA.localeCompare(setB);
  }
  const numA = a.collectorNumber;
  const numB = b.collectorNumber;
  if (numA == null && numB == null) return a.name.localeCompare(b.name);
  if (numA == null) return 1;
  if (numB == null) return -1;
  if (numA !== numB) return numA - numB;
  return a.name.localeCompare(b.name);
}

export default function GalleryView() {
  const [cards, setCards] = useState<CardRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("All");
  const [domainFilter, setDomainFilter] = useState("All");

  const deferredSearch = useDeferredValue(search);

  useEffect(() => {
    fetch("/api/cards")
      .then((r) => r.json())
      .then((data: { cards: CardRecord[] }) => setCards(data.cards ?? []))
      .catch(() => setCards([]))
      .finally(() => setLoading(false));
  }, []);

  const types = useMemo(() => {
    const set = new Set(cards.map((c) => c.type).filter(Boolean) as string[]);
    return ["All", ...[...set].sort()];
  }, [cards]);

  const domains = useMemo(() => {
    const set = new Set(cards.flatMap((c) => c.domains));
    return ["All", ...[...set].sort()];
  }, [cards]);

  const filtered = useMemo(() => {
    return cards
      .filter((c) => {
        if (typeFilter !== "All" && c.type !== typeFilter) return false;
        if (domainFilter !== "All" && !c.domains.includes(domainFilter)) return false;
        return cardMatchesQuery(c, deferredSearch);
      })
      .sort(compareBySetThenNumber);
  }, [cards, deferredSearch, typeFilter, domainFilter]);

  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center gap-3 text-gray-400">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-violet-500 border-t-transparent" />
        Chargement…
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-sm font-semibold uppercase tracking-wider text-violet-400/80">Galerie</p>
          <h1 className="mt-1 text-4xl font-black">
            <span className="bg-gradient-to-r from-violet-400 to-fuchsia-400 bg-clip-text text-transparent">
              Catalogue Riftbound
            </span>
          </h1>
          <p className="mt-2 text-gray-500">
            {filtered.length} cartes · consultation seule, sans quantités
          </p>
        </div>
        <Link
          href="/inventaire"
          className="rounded-2xl border border-cyan-500/30 bg-cyan-500/10 px-5 py-3 text-sm font-bold text-cyan-100 hover:bg-cyan-500/20"
        >
          Gérer l&apos;inventaire →
        </Link>
      </div>

      <div className="mb-6 flex flex-wrap gap-3">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Nom ou numéro (ex. OGN 195)…"
          className="min-w-[220px] flex-1 rounded-xl border border-white/10 bg-black/30 px-4 py-2.5 text-sm outline-none focus:border-violet-500/50"
        />
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="rounded-xl border border-white/10 bg-black/30 px-3 py-2.5 text-sm"
        >
          {types.map((t) => (
            <option key={t} value={t}>
              {t === "All" ? "Tous types" : t}
            </option>
          ))}
        </select>
        <select
          value={domainFilter}
          onChange={(e) => setDomainFilter(e.target.value)}
          className="rounded-xl border border-white/10 bg-black/30 px-3 py-2.5 text-sm"
        >
          {domains.map((d) => (
            <option key={d} value={d}>
              {d === "All" ? "Tous domaines" : d}
            </option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
        {filtered.map((card) => {
          const links = merchantLinksForCard(card);
          return (
            <div key={card.id} className="rounded-2xl border border-white/5 bg-white/[0.03] p-2">
              {card.imageUrl ? (
                <Image
                  src={card.imageUrl}
                  alt={card.name}
                  width={160}
                  height={224}
                  unoptimized
                  className="w-full rounded-xl"
                />
              ) : (
                <div className="flex aspect-[5/7] items-center justify-center rounded-xl bg-black/40 text-gray-600">
                  ?
                </div>
              )}
              <p className="mt-2 truncate text-xs font-semibold">{card.name}</p>
              {(card.setId || card.collectorNumber != null) && (
                <p className="truncate text-[10px] tabular-nums text-gray-500">
                  {[card.setId, card.collectorNumber].filter((v) => v != null && v !== "").join(" ")}
                </p>
              )}
              <div className="mt-1 flex flex-wrap gap-0.5">
                {card.domains.slice(0, 2).map((d) => (
                  <span
                    key={d}
                    className="rounded px-1 text-[9px] font-bold"
                    style={{ color: DOMAIN_COLORS[d]?.text }}
                  >
                    {d}
                  </span>
                ))}
              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                {links.map((link) => (
                  <a
                    key={link.id}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-md border border-white/10 bg-black/30 px-1.5 py-0.5 text-[10px] text-gray-300 hover:bg-white/10"
                  >
                    {link.label}
                  </a>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
